import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationIcon, PlusIcon, SearchIcon } from '@hero/web-icons';
import {
  useAutocompleteApi,
  useRegionAllCitiesApi,
  useRegionCreateApi,
  useRegionSearchApi,
  useTranslateRegions,
} from '@hero/api-hooks';
import type { RegionCity, SearchLocationData } from '@hero/api';
import type { RegionType } from '@hero/schema';
import { cn } from '@hero/web-lib';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet/Sheet';
import { Input } from '../../../ui/Input/Input';
import { RegionName } from '../../../app/RegionName/RegionName';

export interface RegionSelectorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Header title (e.g. "Pick origin", "Pick destination"). */
  title?: string;
  /** Caption under the title. */
  description?: string;
  /** Currently selected `_id` (used to highlight the active row). */
  value?: string | null;
  /** See {@link RegionSelectorProps.types}. */
  types?: RegionType[];
  onPick: (region: RegionCity) => void;
}

/**
 * Drawer-style region picker mirroring the order publish location picker UX.
 *
 * The "in network" list merges two sources:
 * - `useRegionAllCitiesApi` — the platform's reference city list (browse).
 * - `useRegionSearchApi` — Atlas `$search` (fuzzy, aliases, ar/en/ur) once
 *   the user types ≥2 characters, so regions beyond the browse list's cap
 *   are still findable.
 *
 * Typing also queries `useAutocompleteApi` (Google) so users can find a
 * place by its informal name. A Google hit that doesn't map to a backend
 * region is offered as **"Add to network"** — picking it calls the
 * idempotent `region.create` endpoint (created as `needs_review` for
 * non-super-admins, reviewed later in the admin regions queue) and resolves
 * to a real backend region id, which the pricing resolver requires.
 */
export function RegionSelectorDrawer({
  isOpen,
  onClose,
  title,
  description,
  value,
  types,
  onPick,
}: RegionSelectorDrawerProps) {
  const { t } = useTranslation(['m-region', 'm-order', 'c-main']);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  /** `placeId` of the Google row currently being added to the network. */
  const [creatingPlaceId, setCreatingPlaceId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setDebounced('');
      setCreatingPlaceId(null);
      setCreateError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: regions = [], isLoading: isRegionsLoading } = useRegionAllCitiesApi();

  // Atlas search — covers regions beyond the cities list's cap and matches
  // aliases / Arabic / Urdu spellings. Self-disabled under 2 characters.
  const { data: searchHits = [] } = useRegionSearchApi({
    q: debounced,
    types,
    limit: 20,
  });

  const createRegion = useRegionCreateApi();

  const codes = useMemo(() => {
    const all = [
      ...regions.map(r => r.customCode),
      ...searchHits.map(h => h.customCode),
    ];
    return [...new Set(all.filter((c): c is string => !!c))];
  }, [regions, searchHits]);
  const { display: translateRegion } = useTranslateRegions(codes);

  const autocomplete = useAutocompleteApi(
    { search: debounced },
    { enabled: isOpen && debounced.length > 1 },
  );

  const filteredRegions = useMemo(() => {
    const term = debounced.toLowerCase();

    const clientFiltered = term
      ? regions.filter(r => {
          const localized = r.customCode ? translateRegion(r.customCode).toLowerCase() : '';
          const display = (r.displayName ?? '').toLowerCase();
          const code = (r.customCode ?? '').toLowerCase();
          return localized.includes(term) || display.includes(term) || code.includes(term);
        })
      : regions;

    if (!term) return clientFiltered;

    // Atlas hits lead (relevance-ranked); client matches fill in instantly.
    const seen = new Set(searchHits.map(h => String(h._id)));
    const fromSearch: RegionCity[] = searchHits.map(h => ({
      _id: h._id,
      customCode: h.customCode,
      displayName: h.displayName,
    }));
    return [...fromSearch, ...clientFiltered.filter(r => !seen.has(String(r._id)))];
  }, [regions, searchHits, debounced, translateRegion]);

  const otherPlaces = useMemo(() => {
    const places = autocomplete.data?.googlePlaces ?? [];
    if (places.length === 0) return [] as SearchLocationData[];
    const backendNames = new Set(
      [
        ...regions.map(r => r.displayName ?? ''),
        ...searchHits.map(h => h.displayName ?? ''),
      ].map(n => n.toLowerCase()),
    );
    return places.filter(p => !backendNames.has(p.name.toLowerCase())).slice(0, 5);
  }, [autocomplete.data, regions, searchHits]);

  const handlePick = (region: RegionCity) => {
    onPick(region);
    onClose();
  };

  /**
   * Grow the network from a Google pin. `region.create` is idempotent — if
   * the point already falls inside a known region it returns that region —
   * so double-adds and near-duplicates resolve cleanly.
   */
  const handleAddPlace = async (place: SearchLocationData) => {
    if (creatingPlaceId) return;
    setCreatingPlaceId(place.placeId);
    setCreateError(null);
    try {
      const region = await createRegion.mutateAsync({
        location: place.location,
        type: types?.[0] ?? 'city',
      });
      handlePick({
        _id: region._id,
        customCode: region.customCode,
        displayName: region.displayName,
      });
    } catch {
      setCreateError(
        t('drawer.add_place_error', {
          defaultValue: "Couldn't add this place. Try again or pick another result.",
        }),
      );
    } finally {
      setCreatingPlaceId(null);
    }
  };

  const isSearching = autocomplete.isLoading && debounced.length > 1;

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 bg-white w-[480px] sm:max-w-[480px]">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>
            {title ??
              t('drawer.title', { defaultValue: 'Pick a region' })}
          </SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        <div className="border-b px-6 py-3">
          <div className="relative">
            <SearchIcon className="absolute start-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
            <Input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('drawer.search_placeholder', {
                defaultValue: 'Search by region name…',
              })}
              className="ps-10"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isRegionsLoading && regions.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-gray-400">
              {t('c-main:async_operations.loading', { defaultValue: 'Loading…' })}
            </div>
          ) : filteredRegions.length === 0 && !isSearching && otherPlaces.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <p className="text-sm font-medium text-gray-700">
                {t('drawer.empty_title', {
                  defaultValue: 'No matches',
                })}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('drawer.empty_description', {
                  defaultValue:
                    'No regions in our network match your search. Try a different spelling.',
                })}
              </p>
            </div>
          ) : (
            <>
              {filteredRegions.length > 0 && (
                <div>
                  <SectionHeader>
                    {t('drawer.in_network', {
                      defaultValue: 'Regions in our network',
                    })}
                  </SectionHeader>
                  <ul className="divide-y divide-gray-100">
                    {filteredRegions.map(region => {
                      const isSelected = String(region._id) === String(value ?? '');
                      return (
                        <li key={String(region._id)}>
                          <button
                            type="button"
                            onClick={() => handlePick(region)}
                            className={cn(
                              'flex w-full items-center gap-3 px-6 py-3 text-start transition-colors hover:bg-gray-50',
                              isSelected && 'bg-primary/5',
                            )}>
                            <div
                              className={cn(
                                'flex size-8 items-center justify-center rounded-full flex-shrink-0',
                                isSelected
                                  ? 'bg-primary text-white'
                                  : 'bg-primary/10 text-primary',
                              )}>
                              <LocationIcon className="size-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <RegionName
                                customCode={region.customCode}
                                fallback={region.displayName}
                                className="block text-sm font-semibold text-gray-900 truncate"
                              />
                              {region.customCode && (
                                <div className="text-[11px] text-gray-400 uppercase tracking-wide">
                                  {region.customCode}
                                </div>
                              )}
                            </div>
                            {isSelected && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                                {t('c-main:selected', { defaultValue: 'Selected' })}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {isSearching && filteredRegions.length === 0 && (
                <div className="px-6 py-6 text-center text-xs text-gray-400">
                  {t('m-order:wizard.location_picker.searching', {
                    defaultValue: 'Searching…',
                  })}
                </div>
              )}

              {otherPlaces.length > 0 && (
                <div>
                  <SectionHeader>
                    {t('drawer.other_places', {
                      defaultValue: 'Other places — add to our network',
                    })}
                  </SectionHeader>
                  {createError && (
                    <p className="px-6 py-2 text-xs text-red-600">{createError}</p>
                  )}
                  <ul className="divide-y divide-gray-100">
                    {otherPlaces.map(p => {
                      const isAdding = creatingPlaceId === p.placeId;
                      const isBlocked = creatingPlaceId != null && !isAdding;
                      return (
                        <li key={p.placeId}>
                          <button
                            type="button"
                            disabled={isBlocked}
                            onClick={() => handleAddPlace(p)}
                            className={cn(
                              'flex w-full items-center gap-3 px-6 py-3 text-start transition-colors hover:bg-gray-50',
                              isBlocked && 'opacity-50 cursor-not-allowed',
                            )}>
                            <div className="flex size-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 flex-shrink-0">
                              <LocationIcon className="size-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-800 truncate">
                                {p.name}
                              </div>
                              <div className="text-xs text-gray-400 truncate">
                                {p.fullAddress}
                              </div>
                            </div>
                            <span
                              className={cn(
                                'flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                                isAdding
                                  ? 'bg-gray-100 text-gray-500'
                                  : 'bg-primary/10 text-primary',
                              )}>
                              {!isAdding && <PlusIcon className="size-3" />}
                              {isAdding
                                ? t('drawer.adding_place', { defaultValue: 'Adding…' })
                                : t('drawer.add_place', { defaultValue: 'Add' })}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50/95 px-6 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 backdrop-blur">
      {children}
    </div>
  );
}
